import React, { useState } from "react";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MAX_APPLICATION_LINKS, MAX_LINK_NAME_LENGTH, MAX_LINK_URL_LENGTH, emptyApplicationLink, validateApplicationLinks } from "../../../utils/applicationLinks";
import { applicationLinksText } from "./applicationLinksText";
import "./applicationLinks.css";

// Social & Custom Links of a role application (optional section): any number
// of free Name + URL rows. Errors show once a field was left, or everywhere
// when `showErrors` (the form tried to continue).
const ApplicationLinksEditor = ({ links = [], onChange, infoSource, idPrefix = "application-links", showErrors = false }) => {
  const text = applicationLinksText(infoSource);
  const [touched, setTouched] = useState({});
  const { rows, tooMany } = validateApplicationLinks(links);
  const update = (index, field, value) => onChange(links.map((link, position) => (position === index ? { ...link, [field]: value } : link)));
  const remove = (index) => {
    onChange(links.filter((_, position) => position !== index));
    setTouched({});
  };
  const add = () => onChange([...links, emptyApplicationLink()]);
  const touch = (index, field) => () => setTouched((current) => ({ ...current, [`${index}.${field}`]: true }));
  const errorFor = (index, field) => {
    const code = rows[index]?.[field];
    if (!code || !(showErrors || touched[`${index}.${field}`])) return "";
    return text(code).replace("{max}", MAX_LINK_NAME_LENGTH);
  };

  return (
    <section className="application-links" aria-labelledby={`${idPrefix}-title`}>
      <h3 id={`${idPrefix}-title`}>{text("socialLinks")}</h3>
      <p className="application-links-help">{text("socialLinksHelp")}</p>
      {links.map((link, index) => {
        const nameError = errorFor(index, "name");
        const urlError = errorFor(index, "url");
        const id = `${idPrefix}-${index}`;
        return (
          <div className="application-link-row" key={index}>
            <div className={`application-link-field${nameError ? " is-invalid" : ""}`}>
              <label htmlFor={`${id}-name`}>{text("linkName")}</label>
              <input id={`${id}-name`} type="text" value={link.name} maxLength={MAX_LINK_NAME_LENGTH + 20} placeholder={text("linkNamePlaceholder")}
                aria-invalid={nameError ? "true" : "false"} aria-describedby={nameError ? `${id}-name-error` : undefined}
                onChange={(event) => update(index, "name", event.target.value)} onBlur={touch(index, "name")} />
              {nameError && <small id={`${id}-name-error`} role="alert">{nameError}</small>}
            </div>
            <div className={`application-link-field${urlError ? " is-invalid" : ""}`}>
              <label htmlFor={`${id}-url`}>{text("linkUrl")}</label>
              <input id={`${id}-url`} type="url" inputMode="url" value={link.url} maxLength={MAX_LINK_URL_LENGTH} placeholder="https://"
                aria-invalid={urlError ? "true" : "false"} aria-describedby={urlError ? `${id}-url-error` : undefined}
                onChange={(event) => update(index, "url", event.target.value)} onBlur={touch(index, "url")} />
              {urlError && <small id={`${id}-url-error`} role="alert">{urlError}</small>}
            </div>
            <button type="button" className="application-link-remove" onClick={() => remove(index)} aria-label={`${text("removeLink")} ${index + 1}`} title={text("removeLink")}>
              <FaTrashAlt aria-hidden="true" />
            </button>
          </div>
        );
      })}
      {tooMany && <p className="application-links-limit" role="alert">{text("tooManyLinks").replace("{max}", MAX_APPLICATION_LINKS)}</p>}
      <button type="button" className="application-links-add" onClick={add}>
        <FaPlus aria-hidden="true" /> {text("addLink")}
      </button>
    </section>
  );
};

export default ApplicationLinksEditor;
